import {
  Cart,
  CartPropertySearchContextInput,
  DateInput,
  InventoryType,
  PropertyNaturalKeyInput,
  PropertyRoomInput,
  ShoppingPathType
} from '__generated__/api';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';
import { RoomModel } from 'stores/models/room-model';
import { InCartProperty } from 'models/multi-item/multi-item-types';
import { comparer } from 'mobx';
import { withKeys } from 'bernie-core/dist/util/merge';

export function searchCriteriaToPropertyRoomInput(rooms: RoomModel[]): PropertyRoomInput[] {
  if (!rooms) {
    return;
  }

  return rooms.map(room => ({
    childAges: room.children && room.children.map(child => child.age),
    numberOfAdults: room.adults
  }));
}

export function toMultiItemDateType(date): DateInput {
  if (typeof date === 'string') {
    const dateParts = date.split('-');

    return {
      year: Number(dateParts[0]),
      month: Number(dateParts[1]),
      day: Number(dateParts[2])
    };
  }

  if (!(date instanceof Date)) {
    return;
  }

  return {
    day: date.getDate(),
    month: date.getMonth() + 1, //getMonth returns month 0-11
    year: date.getFullYear()
  };
}

export function adaptToPropertyNaturalKeyInput(
  searchCriteria: SearchCriteriaModel,
  inventoryType: string,
  roomTypeCode: string,
  ratePlanCode: string,
  id: string,
  isAttach: boolean
): PropertyNaturalKeyInput {
  if (!searchCriteria) {
    return;
  }

  return {
    checkIn: toMultiItemDateType(searchCriteria.startDate),
    checkOut: toMultiItemDateType(searchCriteria.endDate),
    id,
    inventoryType: adaptInventoryType(inventoryType),
    ratePlanId: ratePlanCode,
    roomTypeId: roomTypeCode,
    rooms: searchCriteriaToPropertyRoomInput(searchCriteria.rooms),
    shoppingPath: isAttach ? ShoppingPathType.ATTACH : ShoppingPathType.STANDALONE
  };
}

export function adaptToCartPropertySearchContextInput(
  searchCriteria: SearchCriteriaModel,
  regionId: string
): CartPropertySearchContextInput {
  return {
    checkIn: toMultiItemDateType(searchCriteria.startDate),
    checkOut: toMultiItemDateType(searchCriteria.endDate),
    childAges: [],
    numberOfAdults: -1,
    regionId
  };
}

export function cartItemToInCartProperty(item: Cart.Items, index: number, allItems: Cart.Items[]): InCartProperty {
  const { id, summary, naturalKey, gallery, offer } = item;
  const unit = offer && offer.units && offer.units[0];
  const ratePlanPriceDetails =
    unit && unit.ratePlans && unit.ratePlans[0] && unit.ratePlans[0].priceDetails && unit.ratePlans[0].priceDetails[0];
  const leadPrice = ratePlanPriceDetails && ratePlanPriceDetails.price && ratePlanPriceDetails.price.lead;
  const priceWithLoyaltyPoints =
    ratePlanPriceDetails && ratePlanPriceDetails.priceAfterLoyaltyPointsApplied && ratePlanPriceDetails.priceAfterLoyaltyPointsApplied.lead;
  const fees =
    ratePlanPriceDetails &&
    ratePlanPriceDetails.fees &&
    ratePlanPriceDetails.fees.filter(fee => fee && fee.description).map(fee => fee.description);
  const minRoomsLeft = ratePlanPriceDetails && ratePlanPriceDetails.availability && ratePlanPriceDetails.availability.minRoomsLeft;
  const available =
    ratePlanPriceDetails &&
    ratePlanPriceDetails.availability &&
    ratePlanPriceDetails.availability.available &&
    (minRoomsLeft === null || minRoomsLeft > numberOfDuplicates(allItems.slice(0, index), item));

  return {
    id,
    name: summary && summary.name,
    checkin: naturalKey && naturalKey.checkIn && naturalKey.checkIn.isoFormat,
    checkout: naturalKey && naturalKey.checkOut && naturalKey.checkOut.isoFormat,
    image: gallery && gallery.featured && gallery.featured.url,
    city: (summary && summary.location && summary.location.address && summary.location.address.city) || '',
    adults: numberOfAdults(naturalKey.rooms),
    children: numberOfChildren(naturalKey.rooms),
    rooms: naturalKey.rooms && naturalKey.rooms.length,
    ratePlan: {
      roomName: unit && unit.name,
      available,
      leadPrice: {
        currencySymbol: leadPrice && leadPrice.currency && leadPrice.currency.symbol,
        formatted: leadPrice && leadPrice.formatted,
        raw: leadPrice && leadPrice.raw
      },
      priceAfterLoyaltyPointsApplied: {
        currencySymbol: priceWithLoyaltyPoints && priceWithLoyaltyPoints.currency && priceWithLoyaltyPoints.currency.symbol,
        formatted: priceWithLoyaltyPoints && priceWithLoyaltyPoints.formatted,
        raw: priceWithLoyaltyPoints && priceWithLoyaltyPoints.raw
      },
      fees
    }
  };
}

function numberOfDuplicates(items: Cart.Items[], item: Cart.Items): number {
  return items
    .filter(otherItem => {
      const compareKeys = ['id', 'roomTypeId', 'ratePlanId', 'checkIn', 'checkOut'];

      return comparer.structural(withKeys(compareKeys).merge({}, item.naturalKey), withKeys(compareKeys).merge({}, otherItem.naturalKey));
    })
    .map(i => i.naturalKey.rooms.length)
    .reduce((acc, roomsCount) => acc + roomsCount, 0);
}

export function numberOfAdults(rooms: Cart.Rooms[]): number {
  if (!rooms || rooms.length === 0) {
    return 0;
  }

  return rooms.reduce((acc, room) => acc + room.numberOfAdults, 0);
}

export function numberOfChildren(rooms: Cart.Rooms[]): number {
  if (!rooms || rooms.length === 0) {
    return 0;
  }

  return rooms.reduce((acc, room) => {
    const roomChildren = room.childAges ? room.childAges.length : 0;

    return acc + roomChildren;
  }, 0);
}

/*
        MERCHANT(1, 24),
        DIRECT_AGENCY(5, 24),
        JUMBOTOURS(9, 81),
        HOTEL_BEDS(10, 82),
        DESPEGAR(11, 84),
        THOMASCOOK_UK(12, 85), **
        TOURICO(13, 86),
        THOMASCOOK_EUR(20, 93), **
        JALAN(26,99),
        HOME_AWAY(28, 83),
        SUNHOTELS(29,104); **
        */
export function adaptInventoryType(inventoryType: string): InventoryType {
  switch (inventoryType) {
    case '1':
      return InventoryType.MERCHANT;
    case '5':
      return InventoryType.DIRECT_AGENCY;
    case '9':
      return InventoryType.JUMBOTOURS;
    case '10':
      return InventoryType.HOTEL_BEDS;
    case '11':
      return InventoryType.DESPEGAR;
    case '13':
      return InventoryType.TOURICO;
    case '26':
      return InventoryType.JALAN;
    case '28':
      return InventoryType.HOME_AWAY;
    default:
      return InventoryType.MERCHANT;
  }
}
